import { useState, useEffect } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { Layout as AntLayout, Menu, Avatar, Dropdown, Badge, Spin } from 'antd'
import ErrorBoundary from './ErrorBoundary'
import { permissionApi } from '../services/api'
import {
  DashboardOutlined,
  ShoppingOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  TeamOutlined,
  InboxOutlined,
  CarOutlined,
  DollarOutlined,
  BarChartOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BellOutlined,
  AppstoreOutlined,
  SafetyOutlined,
  CoffeeOutlined
} from '@ant-design/icons'
import type { MenuProps } from 'antd'

const { Header, Sider, Content } = AntLayout

// 所有菜单项定义（含权限code）
const allMenuItems: MenuProps['items'] = [
  {
    key: '/dashboard',
    icon: <DashboardOutlined />,
    label: '数据概览'
  },
  {
    key: 'product-group',
    icon: <ShoppingOutlined />,
    label: '商品中心',
    children: [
      { key: '/products', label: '商品管理' },
      { key: '/categories', icon: <AppstoreOutlined />, label: '分类管理' },
      { key: '/auxiliary', label: '辅料管理' },
    ]
  },
  {
    key: '/orders',
    icon: <ShoppingCartOutlined />,
    label: '订单管理'
  },
  {
    key: 'customer-group',
    icon: <TeamOutlined />,
    label: '客户管理',
    children: [
      { key: '/customers', label: '个人客户' },
      { key: '/enterprise-clients', label: '企业客户' },
      { key: '/employees', label: '员工管理' },
      { key: '/department-position', label: '部门职位' },
      { key: '/monthly-billing', label: '月结管理' },
    ]
  },
  {
    key: 'inventory-group',
    icon: <InboxOutlined />,
    label: '库存管理',
    children: [
      { key: '/inventory', label: '库存列表' },
      { key: '/warehouses', label: '仓库管理' },
    ]
  },
  {
    key: 'purchase-group',
    icon: <ShoppingCartOutlined />,
    label: '采购管理',
    children: [
      { key: '/purchase', label: '采购订单' },
      { key: '/suppliers', label: '供应商' },
    ]
  },
  {
    key: 'production-group',
    icon: <InboxOutlined />,
    label: '生产管理',
    children: [
      { key: '/production', label: '生产计划' },
      { key: '/bom', label: 'BOM配方' },
    ]
  },
  {
    key: 'delivery-group',
    icon: <CarOutlined />,
    label: '配送管理',
    children: [
      { key: '/delivery', label: '配送任务' },
      { key: '/delivery-drivers', label: '人员管理' },
      { key: '/pickup-points', label: '自提管理' },
    ]
  },
  {
    key: 'hr-group',
    icon: <TeamOutlined />,
    label: '人事管理',
    children: [
      { key: '/attendance', label: '考勤管理' },
      { key: '/leave-requests', label: '请假审批' },
      { key: '/announcements', label: '公告管理' },
      { key: '/crm', label: '销售CRM' },
      { key: '/equipment', label: '设备维护' },
    ]
  },
  {
    key: 'smart-tray-group',
    icon: <CoffeeOutlined />,
    label: '智慧餐盘',
    children: [
      { key: '/smart-tray-orders', label: '订单管理' },
      { key: '/smart-tray-plates', label: '餐盘管理' },
      { key: '/daily-menus', label: '食谱配置' },
      { key: '/dish-ratings', label: '评价分析' },
    ]
  },
  {
    key: 'finance-group',
    icon: <DollarOutlined />,
    label: '财务管理',
    children: [
      { key: '/finance', label: '财务概览' },
      { key: '/finance/expense-records', label: '收支记录' },
      { key: '/finance/bank-accounts', label: '银行账户' },
      { key: '/finance/vouchers', label: '凭证管理' },
      { key: '/finance/statements', label: '财务报表' },
      { key: '/statements', label: '对账单管理' },
      { key: '/receivables', label: '应收款' },
      { key: '/invoices', label: '发票管理' },
      { key: '/purchase-invoices', label: '采购发票' },
    ]
  },
  {
    key: '/reports',
    icon: <BarChartOutlined />,
    label: '报表统计'
  },
  {
    key: 'system-group',
    icon: <SettingOutlined />,
    label: '系统设置',
    children: [
      { key: '/settings', label: '基础设置' },
      { key: '/ads', label: '广告管理' },
      { key: '/permissions', label: '权限管理', icon: <SafetyOutlined /> },
    ]
  },
]

// 根据权限过滤菜单
function filterMenuByPermissions(items: any[], permissionCodes: string[], isSuperAdmin: boolean): MenuProps['items'] {
  if (isSuperAdmin) {
    // 超管看所有菜单，移除permCode属性
    return items.map(item => removePermCode(item))
  }

  return items
    .filter(item => {
      if (item.permCode) {
        return permissionCodes.includes(item.permCode)
      }
      // 如果有children，检查是否有至少一个子项有权限
      if (item.children) {
        return item.children.some((child: any) => 
          !child.permCode || permissionCodes.includes(child.permCode)
        )
      }
      return true // 没有permCode的默认显示
    })
    .map(item => {
      const filtered = { ...item }
      if (filtered.children) {
        filtered.children = filtered.children.filter((child: any) => 
          !child.permCode || permissionCodes.includes(child.permCode)
        ).map((child: any) => removePermCode(child))
      }
      return removePermCode(filtered)
    })
    .filter(item => {
      // 过滤掉空分组
      if (item.children && item.children.length === 0) return false
      return true
    })
}

function removePermCode(item: any) {
  const { permCode, ...rest } = item
  return rest
}

function Layout() {
  const [collapsed, setCollapsed] = useState(false)
  const [menuItems, setMenuItems] = useState<MenuProps['items']>([])
  const [loading, setLoading] = useState(true)
  const [userInfo, setUserInfo] = useState<any>(null)
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    loadPermissions()
  }, [])

  const loadPermissions = async () => {
    try {
      // 先尝试从localStorage读取缓存的权限
      const cachedPerms = localStorage.getItem('userPermissions')
      const cachedUser = localStorage.getItem('user')
      
      if (cachedUser) {
        try { setUserInfo(JSON.parse(cachedUser)) } catch (e) {}
      }

      if (cachedPerms) {
        try {
          const perms = JSON.parse(cachedPerms)
          const filtered = filterMenuByPermissions(allMenuItems || [], perms.permissions || [], perms.is_super_admin || false)
          setMenuItems(filtered)
          setLoading(false)
          return
        } catch (e) {}
      }

      // 从API获取
      const res = await permissionApi.getMyPermissions()
      const perms = res.data || {}
      
      // 缓存权限
      localStorage.setItem('userPermissions', JSON.stringify(perms))
      
      const filtered = filterMenuByPermissions(allMenuItems || [], perms.permissions || [], perms.is_super_admin || false)
      setMenuItems(filtered)
    } catch (e) {
      console.error('获取权限失败', e)
      // 权限获取失败时显示基础菜单（仅dashboard）
      const fallbackMenu = allMenuItems && allMenuItems[0] ? [allMenuItems[0]] : []
      setMenuItems(fallbackMenu)
    }
    setLoading(false)
  }

  const handleUserMenu = ({ key }: { key: string }) => {
    if (key === 'logout') {
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('user')
      localStorage.removeItem('userPermissions')
      navigate('/login')
    }
  }

  const userMenuItems = [
    {
      key: 'profile',
      label: '个人中心',
      icon: <UserOutlined />,
    },
    {
      key: 'logout',
      label: '退出登录',
      icon: <LogoutOutlined />,
      danger: true,
    },
  ]

  // 计算当前展开的菜单组
  const getOpenKeys = () => {
    const path = location.pathname
    for (const item of allMenuItems as any[]) {
      if (item.children) {
        for (const child of item.children) {
          if (child.key === path) return [item.key]
        }
      }
    }
    return ['product-group', 'customer-group', 'system-group']
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="加载权限中..." />
      </div>
    )
  }

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        theme="light"
        style={{
          boxShadow: '2px 0 8px rgba(0,0,0,0.06)',
        }}
      >
        <div style={{ 
          height: 64, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <img 
            src="/logo.png" 
            alt="智厨云" 
            style={{ height: 40, display: collapsed ? 'none' : 'block' }} 
          />
          {collapsed && <span style={{ fontSize: 20, color: '#55266c', fontWeight: 'bold' }}>烹</span>}
        </div>
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          defaultOpenKeys={getOpenKeys()}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
          style={{ borderRight: 0 }}
        />
      </Sider>
      <AntLayout>
        <Header style={{ 
          padding: '0 24px', 
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div 
              onClick={() => setCollapsed(!collapsed)}
              style={{ 
                cursor: 'pointer',
                padding: '8px',
                marginRight: '16px'
              }}
            >
              {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </div>
            <span style={{ fontSize: 16, fontWeight: 500 }}>智厨云管理后台</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Badge count={0} size="small">
              <BellOutlined style={{ fontSize: 18, cursor: 'pointer' }} />
            </Badge>
            <Dropdown menu={{ items: userMenuItems, onClick: handleUserMenu }} placement="bottomRight">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#55266c' }} />
                <span>{userInfo?.real_name || userInfo?.username || '管理员'}</span>
              </div>
            </Dropdown>
          </div>
        </Header>
        <Content style={{
          margin: 24,
          padding: 24,
          background: '#fff',
          borderRadius: 8,
          minHeight: 280
        }}>
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </Content>
        <div style={{
          textAlign: 'center',
          color: '#999',
          fontSize: 13,
          padding: '16px 0',
          borderTop: '1px solid #f0f0f0'
        }}>
          <span>© 2026 智厨云 · 餐饮净菜配送系统</span>
          <span style={{ marginLeft: 12 }}>沪ICP备2026017961号</span>
          <a href="/privacy-policy" style={{ marginLeft: 12, color: '#999', textDecoration: 'underline' }}>隐私协议</a>
        </div>
      </AntLayout>
    </AntLayout>
  )
}

export default Layout
